import type { NavigationMenuItem } from '#ui/types';
import { useStoryblokApi } from '@storyblok/vue';
import type { ComputedRef } from 'vue';

import { STORYBLOK_GLOBAL_STORIES } from '#shared/constants/storyblok';
import { defaultLocale } from '~i18n/i18nConfig';

export type UseStoryblokGlobalConfigReturn = {
    headerMenuItems: ComputedRef<NavigationMenuItem[]>;
    footerExploreLinks: ComputedRef<NavigationMenuItem[]>;
    footerLegalLinks: ComputedRef<NavigationMenuItem[]>;
};

export const _useStoryblokGlobalConfig = async (): Promise<UseStoryblokGlobalConfigReturn> => {
    const { locale, isDefaultLocale } = useTypedI18n();
    const { trackMenuClick } = useTracking();
    const storyblokApiInstance = useStoryblokApi();
    const slug = ref(locale.value);
    const key = computed(() => `globalConfig-${slug.value}`);
    const { data: story, status } = await useAsyncData(
        key,
        () =>
            storyblokApiInstance.getStory(`${STORYBLOK_GLOBAL_STORIES.GLOBAL_CONFIG}/${slug.value}`, {
                version: 'published',
                resolve_links: 'url',
                ...(!isDefaultLocale.value ? { language: locale.value } : {}),
            }),
        {
            watch: [slug],
            transform: result => result.data.story,
        }
    );

    // WATCHERS
    watch(locale, value => (slug.value = value));
    watch(status, value => {
        if (value === 'error') {
            slug.value = defaultLocale;
        }
    });

    // DATA
    const config = computed(() => story.value?.content as StoryblokGlobalConfig | undefined);

    const mapChildItem = (child: StoryblokNavMenuChildItem): NavigationMenuItem => ({
        label: child.label ?? '',
        description: child.description,
        icon: child.icon,
        to: child.link ? storyblokResolveLink(child.link as StoryblokMultilink) : undefined,
        onSelect: () => child.linkClickTrackingText && trackMenuClick(child.linkClickTrackingText),
    });

    const mapItem = (item: StoryblokNavMenuItem): NavigationMenuItem => ({
        label: item.label,
        icon: item.icon,
        to: item.link && !item.children?.length ? storyblokResolveLink(item.link as StoryblokMultilink) : undefined,
        children: item.children?.length ? item.children.map(mapChildItem) : undefined,
        onSelect: () => item.linkClickTrackingText && trackMenuClick(item.linkClickTrackingText),
    });

    return {
        headerMenuItems: computed(() => config.value?.headerMenu?.map(mapItem) ?? []),
        footerExploreLinks: computed(() => config.value?.footerExploreLinks?.map(mapItem) ?? []),
        footerLegalLinks: computed(() => config.value?.footerLegalLinks?.map(mapItem) ?? []),
    };
};

export const useStoryblokGlobalConfig = createSharedComposable(_useStoryblokGlobalConfig);
